import { Controller } from "@hotwired/stimulus"

// Editor de formas da entrada (multi-tender) na simulação da venda direta.
// Ligado: esconde a grade de forma única + o campo de valor da entrada e deixa
// o operador somar várias formas (dinheiro/maquininha). A soma vira a entrada
// (down_payment_amount) e o método vira "misto". Desligado: fluxo normal intacto.
export default class extends Controller {
  static targets = ["toggle", "editor", "rows", "template", "sum", "methodField", "amountField", "status", "submitGuard", "addButton"]
  static values = { index: Number, targetCents: Number }

  // Formas selecionáveis no editor (espelha MISTO_SELECTABLE_KINDS no controller
  // Ruby). Cada uma só pode aparecer UMA vez na entrada — não faz sentido
  // "dinheiro + dinheiro". O limite de linhas é o nº de formas distintas.
  static KINDS = ["cash", "pos", "pix", "credit_card"]

  connect() {
    this.indexValue = this.rowsTarget.querySelectorAll("[data-tender-row]").length
    // Funerary: the entry is CALCULATED (= total_a_pagar). Listen for the live
    // target dispatched by the payment-fields controller and re-validate the sum
    // against it. Cemetery never dispatches this, so targetCents stays 0 and the
    // "sum defines the entrada" behavior below is unchanged.
    this.entryChangedHandler = (event) => {
      this.targetCentsValue = Number(event.detail?.cents) || 0
      this.recalc()
    }
    document.addEventListener("payment-fields:entry-changed", this.entryChangedHandler)
    // Pede o alvo agora que já estamos ouvindo: o payment-fields conecta antes
    // e um anúncio dele teria passado batido.
    document.dispatchEvent(new CustomEvent("payment-tenders:need-target"))
    this.applyMode()
    // O payment-fields decide o que mostrar a partir da forma de pagamento, e
    // no misto quem responde por ela é o campo escondido que o applyMode acabou
    // de habilitar. Sem avisar, a página recarregada com o split ligado abre
    // com todos os outros campos escondidos.
    document.dispatchEvent(new CustomEvent("payment-tenders:mode-applied"))
  }

  disconnect() {
    if (this.entryChangedHandler) {
      document.removeEventListener("payment-fields:entry-changed", this.entryChangedHandler)
    }
  }

  // True for funerary plans: the entrada is CALCULATED (fixed target), not freely
  // typed. Read from the selected product radio so it's independent of the
  // entry-changed event timing. Cemetery products are NOT funerary.
  get isFuneraryProduct() {
    const f = this.form
    if (!f) return false
    const selected = f.querySelector('input[name="direct_sale[product_id]"]:checked')
    return selected?.dataset.productType === "funerary_plan"
  }

  // True for funerary plans: the editor has a fixed entry target to match.
  get hasEntryTarget() {
    return this.isFuneraryProduct
  }

  get form() {
    return this.element.closest("form")
  }

  singleModeInputs() {
    const f = this.form
    if (!f) return []
    const radios = Array.from(f.querySelectorAll('input[name="direct_sale[down_payment_method]"]'))
    const amount = f.querySelector(
      'input[name="direct_sale[down_payment_amount]"]:not([data-payment-tenders-target="amountField"])'
    )
    return amount ? radios.concat(amount) : radios
  }

  // The single-mode "Valor da Entrada" text input (not a radio). Used to keep it
  // enabled for funerary (it carries Adesão Final) while disabling it for cemetery.
  isSingleModeAmount(el) {
    return el.name === "direct_sale[down_payment_amount]" && el.type !== "radio"
  }

  // Seções da forma única (grade de método + valor da entrada), gerenciadas pelo
  // controller payment-fields. Só escondemos quando o split está ligado.
  singleModeSections() {
    const f = this.form
    if (!f) return []
    return Array.from(f.querySelectorAll(
      '[data-payment-fields-target~="downPaymentMethodSection"], [data-payment-fields-target~="downPaymentAmountField"]'
    ))
  }

  toggle() {
    this._userToggled = true
    this.applyMode()
  }

  applyMode() {
    const on = this.toggleTarget.checked
    const funerary = this.isFuneraryProduct
    this.editorTarget.classList.toggle("hidden", !on)
    // Cemetery: the split DEFINES the entrada, so we disable the single-mode
    // amount input and let amountField carry the sum. Funerary: the entrada is
    // CALCULATED (= Adesão Final) and owned by the payment-fields down_payment_amount
    // input — keep it enabled and keep our amountField disabled so we don't submit
    // a blank/duplicate down_payment_amount.
    this.singleModeInputs().forEach((el) => {
      el.disabled = on && !(funerary && this.isSingleModeAmount(el))
    })
    this.methodFieldTarget.disabled = !on
    this.amountFieldTarget.disabled = funerary ? true : !on
    // Desligado: as linhas (down_payment_tenders[]) não devem ir no submit —
    // senão chegam como param "não permitido" e sujam o log. Ligado: enviam o plano.
    this.setRowInputsDisabled(!on)
    if (on) {
      this.singleModeSections().forEach((el) => el.classList.add("hidden"))
      if (this.rowCount() === 0) this.addRow()
    } else if (this._userToggled) {
      // Só re-exibe se foi o operador que desmarcou — no load inicial deixamos o
      // payment-fields controlar a visibilidade (não forçamos exibição).
      this.singleModeSections().forEach((el) => el.classList.remove("hidden"))
    }
    // Desligar o misto sem escolher forma nenhuma mandava o form sem forma, e o
    // servidor mantinha "misto" com as formas antigas. Sem forma marcada, o
    // navegador barra o envio e o aviso de campos faltando diz qual falta.
    const radios = this.singleModeInputs().filter((el) => el.type === "radio")
    const nenhumaMarcada = !radios.some((r) => r.checked)
    radios.forEach((r) => { r.required = !on && this._userToggled && nenhumaMarcada })
    this.syncKindOptions()
    this.updateRemoveButtons()
    this.updateAddButton()
    this.recalc()
  }

  setRowInputsDisabled(disabled) {
    this.rowsTarget.querySelectorAll("select, input").forEach((el) => {
      el.disabled = disabled
    })
  }

  rowCount() {
    return this.rowsTarget.querySelectorAll("[data-tender-row]").length
  }

  addRow() {
    // Não há como adicionar mais formas do que existem tipos distintos.
    if (this.rowCount() >= this.constructor.KINDS.length) return
    // Calcula a forma livre ANTES de inserir — senão a própria linha nova (default
    // "cash") contaria como usada e empurraria a 1ª linha pra "pos".
    const free = this.firstAvailableKind()
    const html = this.templateTarget.innerHTML.replace(/NEW_INDEX/g, this.indexValue)
    this.rowsTarget.insertAdjacentHTML("beforeend", html)
    this.indexValue++
    // A nova linha começa na primeira forma AINDA não usada (evita duplicar a
    // forma default "cash" quando ela já está em outra linha).
    const newSelect = this.rowsTarget.querySelector("[data-tender-row]:last-child [data-tender-kind]")
    if (newSelect && free) {
      newSelect.value = free
      newSelect.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.syncKindOptions()
    this.updateRemoveButtons()
    this.updateAddButton()
    this.recalc()
  }

  removeRow(event) {
    // Mantém pelo menos uma forma — sem linhas a entrada ficaria R$ 0,00 (inválida).
    if (this.rowCount() <= 1) return
    const row = event.target.closest("[data-tender-row]")
    if (row) row.remove()
    this.syncKindOptions()
    this.updateRemoveButtons()
    this.updateAddButton()
    this.recalc()
  }

  // Parcelas por forma: só cartão e maquininha parcelam.
  static INSTALLMENT_KINDS = ["credit_card", "pos"]

  kindChanged(event) {
    const row = event.target.closest("[data-tender-row]")
    if (row) {
      const installments = row.querySelector("[data-tender-installments]")
      if (installments) {
        installments.classList.toggle("hidden", !this.constructor.INSTALLMENT_KINDS.includes(event.target.value))
      }
    }
    this.syncKindOptions()
  }

  // Formas já escolhidas em alguma linha.
  selectedKinds() {
    return Array.from(this.rowsTarget.querySelectorAll("[data-tender-kind]")).map((s) => s.value)
  }

  firstAvailableKind() {
    const used = this.selectedKinds()
    return this.constructor.KINDS.find((k) => !used.includes(k)) || null
  }

  // Impede forma repetida POR CONSTRUÇÃO: em cada select, desabilita as opções já
  // escolhidas em OUTRA linha (mantém habilitada a opção da própria linha).
  syncKindOptions() {
    const selects = Array.from(this.rowsTarget.querySelectorAll("[data-tender-kind]"))
    selects.forEach((select) => {
      const usedElsewhere = selects.filter((s) => s !== select).map((s) => s.value)
      Array.from(select.options).forEach((opt) => {
        opt.disabled = opt.value !== select.value && usedElsewhere.includes(opt.value)
      })
    })
  }

  // Trava o "+ Adicionar" quando todas as formas distintas já estão em uso.
  updateAddButton() {
    if (!this.hasAddButtonTarget) return
    const full = this.rowCount() >= this.constructor.KINDS.length
    this.addButtonTarget.disabled = full
    this.addButtonTarget.title = full ? "Todas as formas já foram adicionadas" : ""
  }

  // Esconde o "×" quando só resta uma linha (não dá pra remover a última).
  updateRemoveButtons() {
    const buttons = this.rowsTarget.querySelectorAll("[data-tender-remove]")
    const hide = buttons.length <= 1
    buttons.forEach((button) => button.classList.toggle("invisible", hide))
  }

  // Preenche esta forma com o que falta para o alvo. O vendedor não deve fazer
  // subtração de cabeça: no funerário o alvo é a entrada calculada, no
  // cemiterial é o valor à vista — os dois chegam pelo mesmo evento.
  fillRemaining(event) {
    event.preventDefault()

    const alvo = this.targetCentsValue
    if (!alvo || alvo <= 0) return

    const linha = event.target.closest("[data-tender-row]")
    const campo = linha?.querySelector("[data-tender-amount]")
    if (!campo) return

    let outras = 0
    this.rowsTarget.querySelectorAll("[data-tender-amount]").forEach((input) => {
      outras += input === campo ? 0 : this.toCents(input.value)
    })

    const falta = alvo - outras
    campo.value = falta > 0 ? (falta / 100).toFixed(2).replace(".", ",") : ""
    campo.dispatchEvent(new Event("input", { bubbles: true }))
  }

  recalc() {
    let cents = 0
    this.rowsTarget.querySelectorAll("[data-tender-amount]").forEach((input) => {
      cents += this.toCents(input.value)
    })
    this.sumTarget.textContent = this.formatBRL(cents)

    if (this.hasEntryTarget) {
      // Funerary: the entrada is CALCULATED (= total_a_pagar). The sum must match
      // the fixed target exactly; we never overwrite down_payment_amount with the
      // sum (it stays = Adesão Final, set by the payment-fields controller).
      this.renderTargetStatus(cents)
      return
    }

    // Cemetery: the sum DEFINES the entrada (no fixed target). O valor à vista
    // aparece como referência — sem ele, "Completar" preenche um número que o
    // vendedor não sabe de onde veio, e ele só descobre que errou no bloqueio.
    if (this.hasStatusTarget) {
      const alvo = this.targetCentsValue
      this.statusTarget.textContent = alvo > 0 ? `Valor negociado: ${this.formatBRL(alvo)} — a soma das formas é a entrada; o que faltar é financiado` : ""
      this.statusTarget.classList.remove("text-green-700", "text-red-600")
      this.statusTarget.classList.add("text-gray-500", "font-normal")
    }

    if (this.toggleTarget.checked) {
      this.amountFieldTarget.value = (cents / 100).toFixed(2).replace(".", ",")
      // A soma É a entrada aqui: quando ela fecha o valor negociado, os campos
      // de financiamento deixam de fazer sentido — mesma regra do campo único.
      // Só avisa quando a soma mudou: o aviso recalcula a cascata, que avisa
      // este editor de volta, e sem o corte vira ciclo até estourar a pilha.
      if (cents !== this._lastSumCents) {
        this._lastSumCents = cents
        document.dispatchEvent(new CustomEvent("payment-tenders:sum-changed"))
      }
    } else {
      this._lastSumCents = undefined
    }
  }

  // Funerary-only: show "Soma: R$X / Alvo R$Y", color red/green, and block the
  // submit (disable the misto method field so the form can't go through as misto)
  // until the sum equals the target to the cent.
  renderTargetStatus(sumCents) {
    const target = this.targetCentsValue
    const matches = sumCents === target
    const on = this.toggleTarget.checked

    const block = on && !matches
    const message = `A soma das formas precisa ser igual ao valor da entrada (${this.formatBRL(target)}).`

    if (this.hasStatusTarget) {
      // Feedback visível (a bolha nativa pode não ancorar no guard sr-only).
      this.statusTarget.textContent = matches
        ? `Soma: ${this.formatBRL(sumCents)} / Alvo ${this.formatBRL(target)}`
        : `⚠ Soma: ${this.formatBRL(sumCents)} / Alvo ${this.formatBRL(target)} — ajuste a soma para igualar o alvo antes de simular`
      // Limpa o estilo de referência do cemiterial: o mesmo elemento serve aos
      // dois casos e o produto pode ser trocado sem recarregar a página.
      this.statusTarget.classList.remove("text-gray-500", "font-normal")
      this.statusTarget.classList.toggle("text-green-700", matches)
      this.statusTarget.classList.toggle("text-red-600", !matches)
    }

    // Block submit on mismatch. The guard is a hidden `required` checkbox: when
    // blocking we enable it + uncheck it + mark required (required+unchecked =
    // invalid → the browser refuses to submit); when valid we disable it so it's
    // inert. setCustomValidity troca a mensagem nativa (em inglês) por pt-BR.
    // Belt-and-suspenders: also disable the misto method field on mismatch.
    if (this.hasSubmitGuardTarget) {
      this.submitGuardTarget.disabled = !block
      this.submitGuardTarget.required = block
      this.submitGuardTarget.checked = !block
      this.submitGuardTarget.title = block ? message : ""
      this.submitGuardTarget.setCustomValidity(block ? message : "")
    }
    if (on) {
      this.methodFieldTarget.disabled = block
    }
  }

  toCents(value) {
    const clean = String(value || "").replace(/[^\d,]/g, "").replace(",", ".")
    const n = parseFloat(clean)
    return isNaN(n) ? 0 : Math.round(n * 100)
  }

  formatBRL(cents) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100)
  }
}
